import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import styled from 'styled-components';
import { message } from 'antd';

import useTranslation from 'customHooks/translate';
import { getInvoiceForPrint, getInvoiceDownload } from 'services/invoices';

import styles from './index.module.css';

import logo from './logo.svg';

const TableContainerStyle = styled.div`
  .MuiTableContainer-root {
    width: 100%;
  }
  .MuiTableCell-body {
    border-bottom: none;
    padding: 10px;
  }
  .MuiTableCell-head {
    padding: 10px;
    opacity: 1;
    color: #acacac;
  }
  .MuiTableRow-root {
    height: 0;
    border-bottom: 1px solid rgba(70, 106, 251, 0.3);
  }
  .MuiTableCell-root {
    background-color: inherit;
    font-size: 10px;
    line-height: inherit;
    font-family: 'DM Sans', sans-serif;
  }
`;

const Print = () => {
  const {
    state: { invoiceId, language },
  } = useLocation();
  const translation = useTranslation();

  const [invoiceData, setInvoiceData] = useState<any>();

  useEffect(() => {
    getInvoiceForPrint(invoiceId, language).then((response) => {
      setInvoiceData(response.data.data);
    });
  }, []);

  const handlePrint = async (invoiceId: any) => {
    message.loading({ content: translation.pdfPreparing, key: 'printLoading' });

    try {
      const response = await getInvoiceDownload(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);

      message.success({ content: translation.pdfReady, key: 'printLoading' });

      const pdfTab = window.open(pdfUrl, '_blank');

      if (pdfTab) {
        pdfTab.onload = () => {
          setTimeout(() => {
            try {
              pdfTab.print();

              pdfTab.addEventListener('afterprint', () => {
                pdfTab.close();
              });

              setTimeout(() => {
                if (!pdfTab.closed) {
                  pdfTab.close();
                }
              }, 30000);
            } catch {
              pdfTab.close();
            }
          }, 1000);
        };

        pdfTab.blur();
        window.focus();

        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 60000);
      } else {
        message.error('Çap səhifəsi açıla bilmədi. Pop-up blocker-i yoxlayın.');
      }
    } catch {
      message.error('Çap zamanı xəta baş verdi');
    }
  };
  return (
    <>
      <header className=" flex h-14 items-center bg-ultramarine-blue px-6">
        <button
          style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '10px 20px',
            fontWeight: '500',
          }}
          onClick={() => handlePrint(invoiceId)}
        >
          Download Invoice to PDF
        </button>
      </header>
      <div className={styles.container__print}>
        <header className={styles.header}>
          <div className={styles.header__info + ' ' + styles.column_view}>
            <h1>{invoiceData?.accountInfo?.accountName}</h1>
            <p className={styles.header__voen}>
              <span>{invoiceData?.accountInfo?.accountLegalDocumentName}:</span>{' '}
              <span>{invoiceData?.accountInfo?.accountLegalDocNumber}</span>
            </p>
            <span>{invoiceData?.accountInfo?.accountStreet}</span>
            <span>
              {invoiceData?.accountInfo?.accountZip},{' '}
              {invoiceData?.accountInfo?.accountCity}
            </span>
            <span>{invoiceData?.accountInfo?.accountCountry}</span>
          </div>
          <div className={styles.header__logo}>
            <img src={logo} alt="Invoice Reporter logo" />
          </div>
        </header>

        <div className={styles.divider + ' ' + styles.center}>
          <span>HESAB FAKTURA</span>
          <span>{invoiceData?.invoiceInfo?.invoiceNumber}</span>
        </div>

        <main className={styles.client}>
          <div>
            <h2 className={styles.client__info__client}>Müştəri:</h2>
            <h2 className={styles.client__info__name}>
              {invoiceData?.invoiceInfo?.partnerInfo?.partnerName}
            </h2>
          </div>
          <div className={styles.client__info}>
            <div className={styles.client__info_col + ' ' + styles.column_view}>
              <p className={styles.client__info__voen}>
                <span>
                  {
                    invoiceData?.invoiceInfo?.partnerInfo
                      ?.partnerLegalDocumentName
                  }
                  :
                </span>{' '}
                <span>
                  {invoiceData?.invoiceInfo?.partnerInfo?.partnerLegalDocNumber}
                </span>
              </p>
              <span className={styles.client__info__info}>
                {invoiceData?.invoiceInfo?.partnerInfo?.partnerStreet}
              </span>
              <span className={styles.client__info__info}>
                {invoiceData?.invoiceInfo?.partnerInfo?.partnerZip},{' '}
                {invoiceData?.invoiceInfo?.partnerInfo?.partnerStreet}
              </span>
              <span className={styles.client__info__info}>
                {invoiceData?.invoiceInfo?.partnerInfo?.partnerCountry}
              </span>
            </div>
            <div
              className={styles.client__info__dates + ' ' + styles.column_view}
            >
              <div>
                <p>Hesab faktura tarixi:</p>
                <span>{invoiceData?.invoiceInfo?.invoiceDate}</span>
              </div>
              <div>
                <p>Son ödəmə tarixi:</p>
                <span>{invoiceData?.invoiceInfo?.lastPaymentDate}</span>
              </div>
              <div>
                <p>Müqavilə No:</p>
                <span>{invoiceData?.invoiceInfo?.contractNumber}</span>
              </div>
              <div>
                <p>Müqavilə tarixi:</p>
                <span>{invoiceData?.invoiceInfo?.contractDate}</span>
              </div>
            </div>
          </div>

          <div className={styles.client__currency}>
            <span>Valyuta {invoiceData?.invoiceInfo?.currency}</span>
          </div>

          <TableContainerStyle>
            <Table stickyHeader aria-label="simple table">
              <TableHead>
                <TableRow>
                  {[
                    'Sıra',
                    'Satış növü',
                    'Açıqlama',
                    'Sayı',
                    'Vahidin qiyməti',
                    'Xalis məbləğ',
                    'Vergi növü',
                    'Vergi məbləği',
                    'Cəmi Məbləğ',
                  ].map((cell) => (
                    <TableCell sx={{ borderBottom: 'none' }}>{cell}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {invoiceData?.invoiceInfo?.invoiceLinesInfos.map((item, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell>{item.rowId}</TableCell>
                    <TableCell>{item.saleType}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.itemPrice}</TableCell>
                    <TableCell>{item.totalPriceAmt}</TableCell>
                    <TableCell>{item.taxType}</TableCell>
                    <TableCell>{item.taxAmt}</TableCell>
                    <TableCell>{item.totalLineAmt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainerStyle>

          <div className={styles.total_cost}>
            <div className={styles.total_cost__container}>
              <div className={styles.total_cost__info}>
                <p>İş NÖMRƏSİ:</p>
                <span>{invoiceData?.invoiceInfo?.invoiceJob}</span>
              </div>
              <div className={styles.total_cost__info}>
                <p>Operator:</p>
                <span>{invoiceData?.invoiceInfo?.manager}</span>
              </div>
            </div>
            <div className={styles.total_cost__container}>
              <div className={styles.total_cost__info}>
                <p>Endirim:</p>
                <span>{invoiceData?.invoiceInfo?.totalDiscountBalance}</span>
              </div>
              <div className={styles.total_cost__info}>
                <p>Cəmi məbləğ:</p>
                <span></span>
              </div>
              <div className={styles.total_cost__info}>
                <p>Cəmi vergi:</p>
                <span> {invoiceData?.invoiceInfo?.totalTaxBalance}</span>
              </div>
            </div>
          </div>
        </main>

        <div className={styles.divider}>
          <div className={styles.total_amount}>
            <span>YEKUN MƏBLƏĞ:</span>
            <div className={styles.total_amount__cost}>
              <p className={styles.total_amount__num}>
                {' '}
                {invoiceData?.invoiceInfo?.totalInvoiceBalance}{' '}
                {invoiceData?.invoiceInfo?.currency}
              </p>
            </div>
          </div>
        </div>

        <footer>
          <div className={styles.bank_info}>
            <div>
              {invoiceData?.invoiceInfo?.bankDataInfo?.bankName && (
                <div>
                  <h2 className={styles.bank_info__bank}>Bank məlumatları::</h2>
                  <div className={styles.column_view}>
                    <p className={styles.bank_info__voen}>
                      <span>VÖEN:</span>{' '}
                      <span>
                        {
                          invoiceData?.invoiceInfo?.bankDataInfo
                            ?.bankLegalDocNumber
                        }
                      </span>
                    </p>
                    <p className={styles.bank_info__voen}>
                      <span>Bankın Adı:</span>{' '}
                      <span>
                        {invoiceData?.invoiceInfo?.bankDataInfo?.bankName}
                      </span>
                    </p>
                    <p className={styles.bank_info__voen}>
                      <span>Kod:</span>{' '}
                      <span>
                        {invoiceData?.invoiceInfo?.bankDataInfo?.bankCode}
                      </span>
                    </p>
                    <p className={styles.bank_info__voen}>
                      <span>İBAN:</span>{' '}
                      <span>
                        {' '}
                        {invoiceData?.invoiceInfo?.bankDataInfo?.iban}
                      </span>
                    </p>
                    <p className={styles.bank_info__voen}>
                      <span>M/H:</span>{' '}
                      <span>
                        {
                          invoiceData?.invoiceInfo?.bankDataInfo
                            ?.correspondentBankAccount
                        }
                      </span>
                    </p>
                    <p className={styles.bank_info__voen}>
                      <span>SWIFT:</span>{' '}
                      <span>
                        {' '}
                        {invoiceData?.invoiceInfo?.bankDataInfo?.swift}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            className={
              styles.divider + ' ' + styles.green + ' ' + styles.center
            }
          >
            invoice_reporter.com proqramında hazırlanmışdır
          </div>
        </footer>
      </div>
    </>
  );
};

export default Print;
